import { useState, useCallback } from 'react';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { parseEventDate } from '../dateTimeUtils';
import type { EventObject } from '../eventTypes';
import type { useUpdateObject } from '@/features/objects/hooks';

/**
 * Drag-and-drop state + handlers for the calendar grid (#3279 Phase B).
 *
 * Owns the `draggedEventId` overlay state and the dnd-kit `onDragStart` /
 * `onDragEnd` callbacks. Extracted verbatim from EventListModule: the
 * callbacks keep their exact `useCallback` dependency arrays so memoization
 * identity is preserved, and the hook is called once at a stable position so
 * Rules-of-Hooks ordering is unchanged.
 *
 * `handleDragEnd`'s dependency array intentionally lists `conversationId`
 * (carried over byte-for-byte from the original) even though the body does
 * not read it — preserving the original memoization cadence.
 */
export interface UseCalendarDndOptions {
  allEvents: EventObject[];
  updateEvent: ReturnType<typeof useUpdateObject<EventObject>>;
  queryClient: QueryClient;
  conversationId: string;
}

export function useCalendarDnd(opts: UseCalendarDndOptions) {
  const { allEvents, updateEvent, queryClient, conversationId } = opts;

  // Drag and drop state for calendar events
  const [draggedEventId, setDraggedEventId] = useState<string | null>(null);

  // Drag and drop handlers for calendar events
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggedEventId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedEventId(null);

    if (!over) return;

    const eventId = active.id as string;
    const targetDateStr = over.id as string;

    // Find the dragged event
    const draggedEvent = allEvents.find(e => e.id === eventId);
    if (!draggedEvent) return;

    // Parse target date (format: "day-YYYY-MM-DD")
    const match = targetDateStr.match(/^day-(\d{4}-\d{2}-\d{2})$/);
    if (!match) return;

    const newDateStr = match[1];
    const oldStart = parseEventDate(draggedEvent.startAt);
    if (!oldStart) return;

    // Calculate new start time, keeping the same time of day
    const [year, month, day] = newDateStr.split('-').map(Number);
    const newStart = new Date(year, month - 1, day, oldStart.getHours(), oldStart.getMinutes());

    // Calculate duration if endAt exists
    let newEnd: Date | null = null;
    if (draggedEvent.endAt) {
      const oldEnd = parseEventDate(draggedEvent.endAt);
      if (oldEnd) {
        const duration = oldEnd.getTime() - oldStart.getTime();
        newEnd = new Date(newStart.getTime() + duration);
      }
    }

    // Update the event
    updateEvent.mutate(
      {
        id: eventId,
        etag: (draggedEvent as any)._etag || (draggedEvent as any).version,
        patch: {
          startAt: newStart.toISOString(),
          ...(newEnd && { endAt: newEnd.toISOString() }),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
        },
      }
    );
  }, [allEvents, updateEvent, queryClient, conversationId]);

  return { draggedEventId, handleDragStart, handleDragEnd };
}
